import React from 'react'
import { Button } from '@mui/material'
import { useAppSelector } from '@SonarCX/hooks/redux'

import BaseModal from '@SonarCX/components/molecules/ChatModals/BaseModal'
import SonarButton from '@SonarCX/components/atoms/Button2'
import Setting from './Setting'

import { nullExpandArray, shiftValue, uniqueArray } from '@SonarCX/utilities'
import {
  ALL_COLUMNS,
  TASK_INCLUDE_FIELD_LIMIT
} from '@SonarCX/providers/TaskPageStateProvider'
import { selectCustomFields } from '@SonarCX/slices/customFields'
import useTaskColumnSettings from '@SonarCX/hooks/useTaskColumnSettings'
import useStyles from './styles'

export const DD_ITEM = 'ORDER'

type TableSettingsModalProps = {
  id: string
  onClose: () => void
}

const TableSettingsModal: React.FC<TableSettingsModalProps> = ({
  id,
  onClose
}) => {
  const { customFields } = useAppSelector(selectCustomFields)

  const {
    columnOrder: savedOrder,
    columnVisible: savedVisible,
    updateColumn: updateProvider
  } = useTaskColumnSettings(id)

  const [order, setOrder] = React.useState(savedOrder)
  const [visible, setVisible] = React.useState(savedVisible)

  const taskCustomFields = React.useMemo(
    () =>
      customFields
        .filter(
          x =>
            x.object_type === 'task' &&
            x.field_type !== 'table' &&
            x.field_type !== 'flow'
        )
        .reduce((acc, curr) => {
          const title = curr.id
          acc[title] = curr.title
          return acc
        }, {} as Record<string, string>),
    [customFields]
  )

  const classes = useStyles()

  React.useEffect(() => {
    setOrder(savedOrder)
  }, [savedOrder])

  React.useEffect(() => {
    setVisible(savedVisible)
  }, [savedVisible])

  const cancel = React.useCallback(() => {
    setOrder(savedOrder)
    setVisible(savedVisible)
    onClose()
  }, [savedOrder, savedVisible, onClose])

  const handleSubmit = React.useCallback(() => {
    updateProvider(visible, order)
    onClose()
  }, [visible, order, updateProvider, onClose])

  const toggleVisible = React.useCallback(
    (key: string) => {
      const storageKey = `TABLE_COLUMN_LAST_ORDER_${id}`
      const storedOrder: Record<string, number> = JSON.parse(
        localStorage.getItem(storageKey) ?? '{}'
      )

      if (visible.includes(key)) {
        setVisible(old => {
          return old.filter(id => id !== key)
        })

        setOrder(old => {
          // Save current position of this item
          const idx = old.findIndex(id => id === key)
          storedOrder[key] = idx
          localStorage.setItem(storageKey, JSON.stringify(storedOrder))

          // Remove from order
          return old.filter(id => id !== key)
        })

        return
      }

      setVisible(old => {
        return uniqueArray([...old, key])
      })

      setOrder(old => {
        // Find old index of this key, or put at end
        const idx = storedOrder[key] ?? order.length
        const copy = [...old]
        copy.splice(idx, 0, key)
        return uniqueArray(copy)
      })
    },
    [id, order, visible]
  )

  const handleDrop = React.useCallback((start, end, onTop) => {
    setOrder(old => {
      // Add filler values to simplify movement logic
      const expanded = nullExpandArray(old)

      // Calculate indices
      const oldIdx = expanded.indexOf(start)
      let newIdx = expanded.indexOf(end)
      newIdx += onTop ? -1 : 1

      // Do movement
      const changed = shiftValue(expanded, oldIdx, newIdx)
      return changed.filter(Boolean)
    })
  }, [])

  const orderSort = React.useCallback(
    ([a, _a], [b, _b]) => {
      const aIndex = order.indexOf(a)
      const bIndex = order.indexOf(b)

      return aIndex - bIndex
    },
    [order]
  )

  const sortedOptions = React.useMemo(() => {
    const entries = Object.entries({ ...ALL_COLUMNS, ...taskCustomFields })
    const visibleEntries = entries.filter(([key]) => visible.includes(key))
    return visibleEntries.sort(orderSort)
  }, [taskCustomFields, visible, orderSort])

  const hiddenOptions = React.useMemo(() => {
    const entries = Object.entries({ ...ALL_COLUMNS, ...taskCustomFields })
    const hiddenEntries = entries.filter(([key]) => !visible.includes(key))
    return hiddenEntries.sort(orderSort)
  }, [visible, orderSort, taskCustomFields])

  const checkIsCustom = React.useCallback(
    (key: string) => key in taskCustomFields,
    [taskCustomFields]
  )

  const customLimitReached = React.useMemo(
    () =>
      sortedOptions.filter(opt => checkIsCustom(opt[0])).length >=
      TASK_INCLUDE_FIELD_LIMIT,
    [sortedOptions, checkIsCustom]
  )

  return (
    // @ts-ignore
    <BaseModal
      title='Task Column Settings'
      {...{ open: true, onClose }}
      footerActions={
        <div className={classes.controls}>
          <Button onClick={cancel} variant='text' className={classes.button}>
            CANCEL
          </Button>

          <SonarButton onClick={handleSubmit} className={classes.button}>
            SAVE
          </SonarButton>
        </div>
      }
    >
      <div className={classes.root}>
        {sortedOptions.map(([key, title]) => (
          <Setting
            key={key}
            id={key}
            onClick={() => toggleVisible(key)}
            onDrop={handleDrop}
            isCustom={checkIsCustom(key)}
            required={key === 'title'}
          >
            {title}
          </Setting>
        ))}

        {hiddenOptions.length > 0 && (
          <div className={classes.hidden}>HIDDEN</div>
        )}

        {hiddenOptions.map(([key, title]) => (
          <Setting
            key={key}
            onClick={() => toggleVisible(key)}
            isCustom={checkIsCustom(key)}
            atLimit={customLimitReached}
            atLimitMessage={`Only ${TASK_INCLUDE_FIELD_LIMIT} custom fields can be shown at once`}
            hidden
          >
            {title}
          </Setting>
        ))}
      </div>
    </BaseModal>
  )
}

export default TableSettingsModal
